import { useEffect, useState } from "react"
import { Navigate, useLocation, useNavigate } from "react-router-dom"
import type { E164Number } from "libphonenumber-js/types.cjs"
import { formatPhoneNumber } from "react-phone-number-input"
import { postJson } from "../api"
import JoinCard from "../components/JoinCard"
import { Step, pathForStep, stepFromPath } from "../joinSteps"
import { clearReferCode, currentReferCode } from "../referCode"
import CodeStep from "../steps/CodeStep"
import DoneStep from "../steps/DoneStep"
import EmailStep from "../steps/EmailStep"
import PhoneStep from "../steps/PhoneStep"
import useFetch from "../useFetch"
import HomePage from "./HomePage"

interface ReferrerResponse {
  name: string
}

export default function LandingPage() {
  const { pathname } = useLocation()
  const navigate = useNavigate()

  const [referCode] = useState(currentReferCode)
  const [referrer, loading, failure] = useFetch<ReferrerResponse>(
    referCode ? `/join/${referCode}` : null
  )

  const [error, setError] = useState("")
  const [phone, setPhone] = useState<E164Number>()
  const [codeSent, setCodeSent] = useState(false)
  const [challengeId, setChallengeId] = useState("")
  const [greeting, setGreeting] = useState("")
  const [submitted, setSubmitted] = useState(false)
  const [approved, setApproved] = useState(false)

  const step = stepFromPath(pathname)

  // Moving between steps, including with the back button, shouldn't carry over
  // an error from the step you just left
  useEffect(() => setError(""), [pathname])

  // A bad code would otherwise wedge this browser on the error screen forever.
  // Only a definite 404 counts; a blip mustn't throw away a good invite.
  useEffect(() => {
    if (failure === "not-found") {
      clearReferCode()
    }
  }, [failure])

  const goTo = (next: Step, replace = false) => navigate(pathForStep(next, referCode), { replace })

  const begin = async () => {
    const res = await postJson("/join/begin", { phone, refer_code: referCode })

    if (!res.success) {
      setError(res.message)
      return false
    }

    // Resy texts the code to this phone number
    setCodeSent(true)
    // Resending from the code step shouldn't stack up history entries
    goTo(Step.Code, step === Step.Code)
    return true
  }

  // A right code usually comes back with Resy asking for the email on the
  // account; occasionally Resy signs them straight in and the join is done.
  const verify = async (code: string) => {
    const res = await postJson<{ challenge_id?: string; approved?: boolean }>("/join/verify", {
      phone,
      code,
      refer_code: referCode,
    })

    if (!res.success) {
      setError(res.message)
      return
    }

    if (res.data?.challenge_id) {
      setChallengeId(res.data.challenge_id)
      setGreeting(res.message)
      goTo(Step.Email)
      return
    }

    done(!!res.data?.approved)
  }

  const confirmEmail = async (email: string) => {
    const res = await postJson<{ approved: boolean }>("/join/email", {
      phone,
      challenge_id: challengeId,
      email,
      refer_code: referCode,
    })

    if (!res.success) {
      setError(res.message)
      return
    }

    done(!!res.data?.approved)
  }

  const done = (approved: boolean) => {
    // The invite has been redeemed, so don't leave it sitting in this browser
    clearReferCode()
    setApproved(approved)
    setSubmitted(true)
    goTo(Step.Done, true)
  }

  if (!referCode) {
    return <HomePage />
  }

  if (loading) {
    return (
      <div className="join-page">
        <div className="spinner-border text-secondary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    )
  }

  if (failure === "error") {
    // The code is still in the URL, so a reload simply retries the lookup
    return (
      <div className="join-page">
        <JoinCard title="Something went wrong" subtitle="We couldn't load your invite.">
          <button
            className="btn btn-primary btn-lg w-100"
            type="button"
            onClick={() => window.location.reload()}
          >
            Try again
          </button>
        </JoinCard>
      </div>
    )
  }

  if (failure === "not-found" || !referrer) {
    return (
      <div className="join-page">
        <JoinCard title="This invite link is invalid" subtitle="Ask your friend for a new one." />
      </div>
    )
  }

  // The flow lives in memory, so a refresh or a deep link starts over. The
  // invite code is carried into the restart so a forwarded link still credits
  // the referrer.
  const restart = <Navigate to={pathForStep(Step.Phone, referCode)} replace />

  if (step === null) {
    return restart
  }
  if (submitted && step !== Step.Done) {
    return <Navigate to={pathForStep(Step.Done, referCode)} replace />
  }
  if (step === Step.Done && !submitted) {
    return restart
  }
  if (step === Step.Code && !codeSent) {
    return restart
  }
  if (step === Step.Email && !challengeId) {
    return restart
  }

  return (
    <div className="join-page">
      {step === Step.Phone && (
        <PhoneStep
          referrerName={referrer.name}
          phone={phone}
          onChange={(value) => {
            setPhone(value)
            setError("")
          }}
          onSubmit={begin}
          error={error}
        />
      )}

      {step === Step.Code && (
        <CodeStep
          phone={phone ? formatPhoneNumber(phone) : ""}
          onSubmit={verify}
          onResend={begin}
          onBack={() => goTo(Step.Phone)}
          error={error}
          clearError={() => setError("")}
        />
      )}

      {step === Step.Email && (
        <EmailStep
          greeting={greeting}
          onSubmit={confirmEmail}
          onBack={() => goTo(Step.Phone)}
          error={error}
          clearError={() => setError("")}
        />
      )}

      {step === Step.Done && <DoneStep approved={approved} />}
    </div>
  )
}
